// components/NotificationBell/NotificationItem.tsx
import React from 'react';
import { List, Button, Typography, Space, Tag } from 'antd';
import { CheckOutlined, PhoneOutlined, HomeOutlined } from '@ant-design/icons';
import { UserInfoNotification } from '../../types/notifications';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { useTheme } from '../../contexts/ThemeContext';

const { Text } = Typography;

interface NotificationItemProps {
  notification: UserInfoNotification;
  onResolve: (id: string) => void;
  loading: boolean;
}

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onResolve,
  loading
}) => {
  const { theme } = useTheme();
  const isResolved = notification.status === 'RESOLVED';

  const formatDate = (date: string | Date) => {
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true, locale: es });
    } catch {
      return 'Fecha inválida';
    }
  };

  return (
    <List.Item
      style={{
        padding: '12px 16px',
        borderBottom: `1px solid ${theme === 'dark' ? '#333' : '#f0f0f0'}`,
        backgroundColor: isResolved
          ? (theme === 'dark' ? '#1a1a1a' : '#fafafa')
          : (theme === 'dark' ? '#141414' : '#fff'),
        opacity: isResolved ? 0.7 : 1,
        transition: 'background-color 0.2s',
      }}
      actions={[
        !isResolved && (
          <Button
            key="resolve"
            type="primary"
            size="small"
            icon={<CheckOutlined />}
            onClick={() => onResolve(notification.id)}
            loading={loading}
          >
            Resolver
          </Button>
        ),
      ].filter(Boolean)}
    >
      <List.Item.Meta
        title={
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Space>
              <Text strong>{notification.username || 'Sin nombre'}</Text>
              {isResolved && <Tag color="success">Resuelta</Tag>}
              {!isResolved && <Tag color="processing">Pendiente</Tag>}
            </Space>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {formatDate(notification.createdAt)}
            </Text>
          </Space>
        }
        description={
          <Space direction="vertical" size={4} style={{ marginTop: 8 }}>
            {notification.phone && (
              <Space>
                <PhoneOutlined style={{ color: '#52c41a' }} />
                <Text style={{ fontSize: '13px' }}>
                  <strong>Tel Personal:</strong> {notification.phone}
                </Text>
              </Space>
            )}
            {notification.phoneOffice && (
              <Space>
                <PhoneOutlined style={{ color: '#fa8c16' }} />
                <Text style={{ fontSize: '13px' }}>
                  <strong>Tel Oficina:</strong> {notification.phoneOffice}
                </Text>
              </Space>
            )}
            {notification.officeName && (
              <Space>
                <HomeOutlined style={{ color: '#722ed1' }} />
                <Text style={{ fontSize: '13px' }}>
                  <strong>Oficina:</strong> {notification.officeName}
                </Text>
              </Space>
            )}
            {isResolved && notification.resolvedAt && (
              <Text type="secondary" style={{ fontSize: '12px', fontStyle: 'italic' }}>
                Resuelta {formatDate(notification.resolvedAt)}
              </Text>
            )}
          </Space>
        }
      />
    </List.Item>
  );
};
